import type { Zone } from '@seek/audience-zones';
import type { SearchParams } from '@seek/chalice-types';
import type { Locale } from '@seek/melways-sites';

import getCountryFromZone from 'src/config/utils/getCountryFromZone';
import clean from 'src/modules/clean-object';
import { convertToApiQuery } from 'src/modules/seek-jobs-search-query';
import { shouldRequestRelatedSearches } from 'src/modules/seek-jobs-search-query/convertToApiQueryHelper';
import type { Country } from 'src/types/globals';

// This function builds the API query parameters for the job search API
// The function is shared across /search, /count and /counts.
export const buildApiQuery = ({
  baseKeywords,
  country,
  locale,
  newSince,
  queryHints,
  relatedSearchesCount,
  searchQuery,
  seekerId,
  sessionId,
  solId,
  source,
  userClientId,
  userQueryId,
  zone,
}: {
  baseKeywords?: string;
  country: Country;
  locale: Locale;
  newSince?: number;
  queryHints?: string;
  relatedSearchesCount?: number;
  searchQuery: SearchParams;
  seekerId?: number;
  sessionId: string;
  solId: string;
  source?: string;
  userClientId: string;
  userQueryId?: string;
  zone: Zone;
}) => {
  const tags = searchQuery.tags;
  const analyticsParams = {
    eventCaptureSessionId: sessionId || null,
    userid: userClientId || null,
    userqueryid: userQueryId || null,
    usersessionid: userClientId || null,
  };

  // TODO-ZONES: See if we can update the library so we can pass in Zone
  // instead of country value
  const searchParams = convertToApiQuery({
    country,
    searchQuery,
    zone,
  });

  const isLocationOnlySerp = Boolean(
    !searchQuery.keywords && !searchQuery.classification && searchQuery.where,
  );

  const isLocationAndWorkTypeSerp = Boolean(
    isLocationOnlySerp && searchQuery.worktype,
  );

  const requestFacets = isLocationOnlySerp || isLocationAndWorkTypeSerp;

  const cleanApiQuery = clean({
    newSince,
    siteKey: `${getCountryFromZone(zone)}-Main`,
    sourcesystem: 'houston',
    facets: requestFacets ? 'distinctTitle' : '',
    ...analyticsParams,
    ...{
      ...searchParams,
      baseKeywords: baseKeywords ? baseKeywords : searchParams?.keywords,
    },
    ...{
      tags,
    },
    locale,
    seekerId,
    solId,
    ...(shouldRequestRelatedSearches({ searchParams, zone }) && {
      relatedSearchesCount,
    }),
    ...(source && { source }),
    ...(queryHints && { queryHints }),
  });

  return {
    ...cleanApiQuery,
    baseKeywords: cleanApiQuery?.baseKeywords
      ? cleanApiQuery?.baseKeywords
      : '',
  };
};
